import { useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT = [
  { value: "van", label: "Dry Van" },
  { value: "reefer", label: "Reefer" },
  { value: "flatbed", label: "Flatbed" },
  { value: "power_only", label: "Power Only" },
];

const inputCls =
  "w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/60";

export default function PostLoadPage() {
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "", mc_number: "",
    origin_city: "", origin_state: "", destination_city: "", destination_state: "",
    equipment: "van", pickup_date: tomorrow, weight_lbs: "", miles: "",
    posted_rate: "", commodity: "", website: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reposting, setReposting] = useState(false);
  const [activeCount, setActiveCount] = useState(null);

  useEffect(() => {
    document.title = "Post a Load Free — FreightPilot AI";
    axios.get(`${API}/api/public/posted-loads/count`)
      .then(({ data }) => setActiveCount(data.active))
      .catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const src = params.get("src");
    if (src && !sessionStorage.getItem(`fp_src_${src}`)) {
      sessionStorage.setItem(`fp_src_${src}`, "1");
      axios.post(`${API}/api/public/post-load/track`, { src }).catch(() => {});
    }
    const repostId = params.get("repost");
    const token = params.get("token");
    if (repostId && token) {
      axios.get(`${API}/api/public/post-load/${repostId}/repost-data?token=${token}`)
        .then(({ data }) => {
          setForm((f) => ({
            ...f, ...Object.fromEntries(
              Object.entries(data).map(([k, v]) => [k, v === null || v === 0 ? "" : String(v)])),
            pickup_date: f.pickup_date, website: "",
          }));
          setReposting(true);
        })
        .catch(() => {});
    }
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/post-load`, {
        ...form,
        origin_state: form.origin_state.toUpperCase(),
        destination_state: form.destination_state.toUpperCase(),
        weight_lbs: form.weight_lbs ? parseInt(form.weight_lbs, 10) : null,
        miles: form.miles ? parseInt(form.miles, 10) : null,
        posted_rate: parseFloat(form.posted_rate),
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — check the fields and try again");
    } finally {
      setBusy(false);
    }
  };

  const shareText =
    "🚛 FREE load board for shippers & owner-operators — post your load in 60 seconds, carriers call you direct. No fees, no middleman. https://freightpilotai.ai/post-load?src=share";

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) { /* clipboard blocked */ }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Post a Load Free — FreightPilot AI", text: shareText, url: "https://freightpilotai.ai/post-load?src=share" });
      } catch (e) { /* user cancelled */ }
    } else {
      copyShare();
    }
  };

  const ShareBlock = () => (
    <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="post-load-share-block">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Know truckers who'd want this?</div>
      <img src="/post-load-share.png" alt="Post a load free on FreightPilot AI" className="rounded-lg border border-white/10 w-full max-w-sm mb-3" data-testid="post-load-share-image" />
      <p className="text-xs text-neutral-400 leading-relaxed mb-3">"{shareText}"</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={copyShare} data-testid="post-load-copy-share"
          className="rounded-full px-4 py-2 text-xs font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038]">
          {copied ? "Copied ✓" : "Copy post text"}
        </button>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://freightpilotai.ai/post-load")}&quote=${encodeURIComponent(shareText)}`}
          target="_blank" rel="noreferrer" data-testid="post-load-fb-share"
          className="rounded-full px-4 py-2 text-xs font-semibold border border-white/15 text-neutral-300 hover:border-[#1877F2] hover:text-[#1877F2]">
          Share to Facebook
        </a>
        <button onClick={nativeShare} data-testid="post-load-native-share"
          className="rounded-full px-4 py-2 text-xs font-semibold border border-white/15 text-neutral-300 hover:border-white/40">
          Share…
        </button>
      </div>
    </div>
  );

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center" data-testid="post-load-success">
          <div className="text-5xl mb-4">🚛</div>
          <h1 className="font-display text-2xl font-bold mb-3">Your load is live!</h1>
          <p className="text-neutral-400 text-sm leading-relaxed mb-2">
            Carriers on FreightPilot can see it right now and will call or email you directly.
            We sent a confirmation to <b className="text-white">{form.email}</b> with a one-click
            link to mark it covered.
          </p>
          <p className="text-neutral-500 text-xs mb-6">It auto-expires 2 days after pickup if you do nothing.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setDone(false); setForm({ ...form, origin_city: "", origin_state: "", destination_city: "", destination_state: "", posted_rate: "", miles: "", weight_lbs: "", commodity: "" }); }}
              data-testid="post-another-btn"
              className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038]"
            >
              Post another load
            </button>
            <a href="/" className="rounded-full px-5 py-2.5 text-sm border border-white/15 text-neutral-300 hover:border-white/30">
              What is FreightPilot?
            </a>
          </div>
          <ShareBlock />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <a href="/" className="text-sm text-neutral-500 hover:text-white">← FreightPilot AI</a>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-4 mb-2">
          Post a load. <span className="text-[#FF5A1F]">Free.</span>
        </h1>
        <p className="text-neutral-400 text-sm leading-relaxed mb-1">
          Got freight to move? Post it here in 60 seconds — no account, no fees, no middleman.
          Carriers running FreightPilot see it instantly and contact you directly.
        </p>
        {activeCount !== null && activeCount > 0 && (
          <p className="text-emerald-400 text-xs mb-6" data-testid="active-loads-count">
            ● {activeCount} load{activeCount !== 1 ? "s" : ""} live on the board right now
          </p>
        )}
        {reposting && (
          <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300" data-testid="repost-banner">
            👋 Welcome back! Your lane is pre-filled — just confirm the pickup date and rate, then hit post.
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-5" data-testid="post-load-form">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Who's posting</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required value={form.company_name} onChange={set("company_name")} placeholder="Company name *" data-testid="pl-company" className={inputCls} />
              <input value={form.contact_name} onChange={set("contact_name")} placeholder="Contact name" data-testid="pl-contact" className={inputCls} />
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email * (carriers reply here)" data-testid="pl-email" className={inputCls} />
              <input value={form.phone} onChange={set("phone")} placeholder="Phone (carriers call faster)" data-testid="pl-phone" className={inputCls} />
              <input value={form.mc_number} onChange={set("mc_number")} placeholder="MC # (optional, builds trust)" data-testid="pl-mc" className={inputCls} />
            </div>
            {/* honeypot — hidden from humans */}
            <input value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off"
              className="absolute opacity-0 pointer-events-none h-0 w-0" placeholder="website" aria-hidden="true" />
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">The load</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <input required value={form.origin_city} onChange={set("origin_city")} placeholder="Origin city *" data-testid="pl-origin-city" className={inputCls} />
              <input required maxLength={2} value={form.origin_state} onChange={set("origin_state")} placeholder="ST *" data-testid="pl-origin-state" className={`${inputCls} uppercase`} />
              <input required value={form.destination_city} onChange={set("destination_city")} placeholder="Destination city *" data-testid="pl-dest-city" className={inputCls} />
              <input required maxLength={2} value={form.destination_state} onChange={set("destination_state")} placeholder="ST *" data-testid="pl-dest-state" className={`${inputCls} uppercase`} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <select value={form.equipment} onChange={set("equipment")} data-testid="pl-equipment" className={inputCls}>
                {EQUIPMENT.map((eq) => <option key={eq.value} value={eq.value}>{eq.label}</option>)}
              </select>
              <input required type="date" value={form.pickup_date} onChange={set("pickup_date")} data-testid="pl-pickup-date" className={inputCls} />
              <input type="number" min="0" value={form.weight_lbs} onChange={set("weight_lbs")} placeholder="Weight (lbs)" data-testid="pl-weight" className={inputCls} />
              <input type="number" min="0" value={form.miles} onChange={set("miles")} placeholder="Miles (approx)" data-testid="pl-miles" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required type="number" min="1" step="1" value={form.posted_rate} onChange={set("posted_rate")} placeholder="Rate you're offering ($) *" data-testid="pl-rate" className={inputCls} />
              <input value={form.commodity} onChange={set("commodity")} placeholder="What is it? (e.g. palletized produce)" data-testid="pl-commodity" className={inputCls} />
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="post-load-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="post-load-submit"
            className="w-full rounded-full px-6 py-3.5 text-base font-bold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-60 transition-colors">
            {busy ? "Posting…" : "Post my load — free"}
          </button>
          <p className="text-center text-[11px] text-neutral-600">
            No spam, no fees. Your load auto-expires after pickup. You'll get a one-click link to remove it any time.
          </p>
        </form>

        <ShareBlock />
        <GroupPostPack />
      </div>
    </div>
  );
}

const GROUP_POSTS = [
  { tone: "Straight talk", src: "fb-straight", text: "If you're a shipper or broker with freight sitting, there's a free load board at freightpilotai.ai/post-load?src=fb-straight — post in 60 seconds, carriers call you direct. No fees, no signup. That's it, that's the post." },
  { tone: "Owner-op angle", src: "fb-owner", text: "Owner-ops: loads posted at freightpilotai.ai/post-load?src=fb-owner come straight from shippers — no load board subscription needed to see them, no middleman shaving the rate. Free to post, free to book. Pass it to any shipper you know." },
  { tone: "Shipper angle", src: "fb-shipper", text: "Shippers: skip the broker markup. Post your load free at freightpilotai.ai/post-load?src=fb-shipper and carriers running FreightPilot get an instant alert on your lane. You keep the difference." },
  { tone: "Humor", src: "fb-humor", text: "Load boards charging $150/month to show you the same 4 loads 🙃 Meanwhile freightpilotai.ai/post-load?src=fb-humor is out here FREE — shippers post, carriers call, nobody takes a cut. Wild concept, we know." },
  { tone: "Community", src: "fb-community", text: "We built a free load board for the small guys — owner-ops and small fleets who shouldn't need 3 subscriptions to find freight. If you've got a load to move (or know someone who does): freightpilotai.ai/post-load?src=fb-community. Help us fill it up 🚛" },
];

function GroupPostPack() {
  const [copiedIdx, setCopiedIdx] = useState(-1);
  const [clicks, setClicks] = useState({});
  useEffect(() => {
    axios.get(`${API}/api/public/post-load/click-stats`)
      .then(({ data }) => setClicks(data.clicks || {}))
      .catch(() => {});
  }, []);
  const copy = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx(-1), 2000);
    } catch (e) { /* clipboard blocked */ }
  };
  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="group-post-pack">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Facebook group post pack</div>
      <p className="text-xs text-neutral-500 mb-3">5 ready-to-paste posts in different tones — each has its own tracked link, so you'll see which one pulls clicks.</p>
      <div className="space-y-2">
        {GROUP_POSTS.map((p, i) => (
          <div key={i} className="flex items-start gap-2 rounded-lg bg-neutral-900/60 border border-white/[0.06] p-3">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider text-[#FF9764] mb-0.5">
                {p.tone}
                {clicks[p.src] > 0 && (
                  <span data-testid={`group-post-clicks-${i}`} className="ml-2 normal-case tracking-normal rounded-full bg-emerald-500/15 text-emerald-300 px-2 py-0.5 text-[10px] font-semibold">
                    {clicks[p.src]} click{clicks[p.src] === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div className="text-xs text-neutral-300 leading-relaxed">{p.text}</div>
            </div>
            <button onClick={() => copy(p.text, i)} data-testid={`group-post-copy-${i}`}
              className="shrink-0 rounded-full px-3 py-1.5 text-[11px] font-semibold border border-white/15 text-neutral-300 hover:border-[#FF5A1F] hover:text-[#FF9764]">
              {copiedIdx === i ? "Copied ✓" : "Copy"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
